import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { ImageSet } from '../../types/campaign';

/** The merge token a template's image field uses to pick up each recipient's team image. */
export const CAMPAIGN_IMAGE_TOKEN = '{{campaign_image_url}}';

// Same token grammar as the merge engine (whitespace after the braces, optional
// "|fallback"), restricted to this one field name.
const IMAGE_TOKEN_RE = /\{\{\s*campaign_image_url\s*(?:\|[^}]*)?\}\}/;

interface ImageSetPickerProps {
  /** Selected set key, or '' for none. */
  value: string;
  onChange: (setKey: string) => void;
  imageSets: ImageSet[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  disabled: boolean;
  /** The selected template's image field, used to say whether it will pick up the set. */
  templateImageUrl: string | undefined;
  hasTemplate: boolean;
}

/**
 * Optional per-team image set for a campaign. The image field itself lives on
 * the email template (edited on the templates page), so this card only selects
 * the set and tells the author whether the chosen template will use it.
 */
export function ImageSetPicker({
  value, onChange, imageSets, isLoading, error, onRetry, disabled, templateImageUrl, hasTemplate,
}: ImageSetPickerProps) {
  const selectedInList = imageSets.some((s) => s.setKey === value);
  const usesToken = IMAGE_TOKEN_RE.test(templateImageUrl ?? '');
  const showTemplateWarning = !!value && hasTemplate && !usesToken;
  const describedBy = [value ? 'campaign-image-set-help' : null, showTemplateWarning ? 'campaign-image-set-warning' : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-2">
      <label className="label mb-1" htmlFor="campaign-image-set">Image set</label>
      <div className="flex items-center gap-2">
        <select
          id="campaign-image-set"
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || isLoading}
          aria-describedby={describedBy}
        >
          <option value="">None</option>
          {value && !selectedInList && (
            <option value={value}>{isLoading || error ? value : `${value} (no longer published)`}</option>
          )}
          {imageSets.map((s) => (
            <option key={s.setKey} value={s.setKey}>{`${s.name} (v${s.version} · ${s.imageCount} image${s.imageCount === 1 ? '' : 's'})`}</option>
          ))}
        </select>
        {isLoading && (
          <span role="status">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" aria-hidden="true" />
            <span className="sr-only">Loading image sets</span>
          </span>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          <button type="button" className="btn-secondary text-xs" onClick={onRetry}>Retry</button>
        </div>
      )}

      {!isLoading && !error && imageSets.length === 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          No published image sets yet — create one in Command Stores → Marketing images.
        </p>
      )}

      {value && (
        <p id="campaign-image-set-help" className="text-xs text-gray-500 dark:text-gray-400">
          Each person gets their own team's image. Use <code>{CAMPAIGN_IMAGE_TOKEN}</code> in the template's image field.
        </p>
      )}
      {showTemplateWarning && (
        <p id="campaign-image-set-warning" className="text-xs text-amber-700 dark:text-amber-400">
          This template's image field doesn't use <code>{CAMPAIGN_IMAGE_TOKEN}</code>, so the set won't appear.{' '}
          <Link to="/campaigns/templates" className="text-blast-600 dark:text-blast-400 hover:underline">Edit the template →</Link>
        </p>
      )}
    </div>
  );
}
